import { createRouter, createWebHistory } from 'vue-router';
import { auth } from '@/firebaseConfig';
import { ENGINE_CUTOVER } from '@/config/engineFlags';
import { toCanonical, enginePath, MEMBER_LANDING } from '@/utils/engineNav';
import TermsOfService from '@/views/TermsOfService.vue';
import RefundPolicy from '@/views/RefundPolicy.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';

// 챌린지 is where a member lands, and a brand-new member's first stop is the
// profile step, which the guard below runs from the home route. So the 챌린지
// tab hands a member who has not seen that step to /home, and the existing
// flow takes over unchanged (it reads the same two stamps).
async function profileStepFirst(to, from, next) {
  try {
    const { default: userService } = await import('@/api/userService');
    const u = auth.currentUser && await userService.getUser(auth.currentUser.uid);
    if (u && !u.profileCompletedAt && !u.profileOnboardingShownAt) { next(enginePath('/home')); return; }
  } catch (e) { /* fail open, as the payment check does */ }
  next();
}

// The new 챌린지 program (PR 1a): /today is for program accounts only
// (programSettings/global and the beta list). Anyone else lands where a member
// lands. Fails closed: the program is opt-in.
async function programGate(to, from, next) {
  try {
    const { ensureProgram } = await import('@/services/programStore');
    if (await ensureProgram(auth.currentUser && auth.currentUser.uid) === 'in') { next(); return; }
  } catch (e) { /* out */ }
  next(MEMBER_LANDING);
}

// Setup routes
const routes = [
  // DEV-ONLY: visual harness for the home-revamp components (fixture props,
  // no auth). Excluded from production builds entirely.
  ...(process.env.NODE_ENV !== 'production'
    ? [{
        path: '/__revamp-preview',
        name: 'RevampPreview',
        component: () => import('@/views/RevampPreview.vue'),
      }]
    : []),
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/views/LandingPage.vue'),
  },
  {
    path: '/membership',
    name: 'Membership',
    component: () => import('@/views/MembershipPage.vue'),
  },
  // The legacy home. Behind ENGINE_CUTOVER it retires and the engine home
  // takes /home itself (canonicalRoutes, below): the bookmarked and
  // KakaoTalk-linked /home lands on the new home at its own address, never on
  // a redirect to /home-next (Eddie, 2026-09-20).
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/HomePage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // The five tab views used to carry webpackPrefetch (2026-09-16). Webpack
  // emits those links in the ENTRY chunk, so they fired on the public landing
  // page too: 887 KB of member-only code pulled down before anyone had signed
  // in (measured in the pre-merge audit, 2026-09-17). The warming moved behind
  // the auth gate — EngineTabBar requests the same five chunks once it knows
  // there is a member — which keeps the win (the first tap never waits on a
  // download) and stops spending a visitor's bandwidth on it.
  // Daily session engine (docs/engine/). Deliberately absent from the nav
    // and the App.vue header allowlists until the PR-F flip — reachable by
    // URL only, which is the route-flag for this build. ?mode=free runs the
    // unscheduled 자유 복습 (never touches due dates).
    path: '/session',
    name: 'EngineSession',
    component: () => import('@/views/EngineSessionView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // Engine map home (PR D) — same URL-only flag as /session: absent from
    // every nav and from the App.vue header allowlists until the PR-F flip,
    // when this becomes /home and the legacy home retires.
    path: '/home-next',
    name: 'EngineHome',
    component: () => import('@/views/EngineHomeView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // The expression arc (V2 Stage 3 item 2) — URL-only. Currently the
    // header mini-gate build: /arc-next?sit=&x=&phase=video|learn|apply&prog=
    path: '/arc-next',
    name: 'EngineArc',
    component: () => import('@/views/EngineArcView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // 상황 복습 (V2 Stage 3, first build) — standalone, URL-only like its
    // siblings: /review-next?sit=<situationId>. Absent from every nav until
    // the arc wires it in; the same view will serve the 3-day return.
    path: '/review-next',
    name: 'EngineReview',
    component: () => import('@/views/EngineReviewView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // 수업 archive (F piece, flagged like its siblings until the re-flip).
    path: '/lessons',
    name: 'Lessons',
    component: () => import('@/views/EngineLessonsView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // 더보기 탭 (brief §2): 자료실 · 커뮤니티 · 미션 · 프로필/설정.
    path: '/more-next',
    name: 'EngineMore',
    component: () => import('@/views/EngineMoreView.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/payment',
    name: 'Payment',
    component: () => import('@/views/PaymentPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/payment/manage',
    name: 'PaymentManage',
    component: () => import('@/views/PaymentManagePage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: () => import('@/views/PaymentSuccess.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/payment/fail',
    name: 'PaymentFail',
    component: () => import('@/views/PaymentFail.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: () => import('@/views/SignUpPage.vue'),
    beforeEnter: (to, from, next) => {
      if (auth.currentUser) {
        next({ path: MEMBER_LANDING }); // already signed in: straight to the member landing (챌린지)
      } else {
        next(); // Allow navigation to sign up if not logged in
      }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    beforeEnter: (to, from, next) => {
      if (auth.currentUser) {
        next({ path: MEMBER_LANDING }); // already signed in: straight to the member landing (챌린지)
      } else {
        next(); // Allow navigation to sign up if not logged in
      }
    }
  },
  {
    path: '/admin/invites',
    name: 'InviteManagement',
    component: () => import('@/views/InviteManagementPage.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/analytics',
    name: 'BookAnalytics',
    component: () => import('@/views/AdminBookAnalyticsPage.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/memberships',
    name: 'MembershipsManagement',
    component: () => import('@/views/AdminMembershipsPage.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: () => import('@/views/AdminDashboardPage.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      requiresOwner: true
    }
  },
  {
    path: '/admin/missions',
    name: 'AdminMissions',
    component: () => import('@/views/AdminMissionsPage.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/books/:category/:id?',
    name: 'Books',
    component: () => import('@/views/PdfBookPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    },
    beforeEnter: (to, from, next) => {
        // 라이브 다시보기 + 비즈 퀴즈: video archives restored 2026-08-01 —
        // they lost their only surface when HomePanel's 더 보기 was deleted.
        const allowedCategories = ['비즈영어 표현집', '월간 실무 자료', '라이브 다시보기', '비즈 퀴즈'];
          const category = to.params.category;
          if (allowedCategories.includes(category)) {
              next(); // Allow navigation if category is in the allowed list
          } else {
              // Default = the first member pill (세션 다시보기; route value is
              // the stable '라이브 다시보기' segment — the 2026-08-02 rename is
              // label-only). Tier-0 is separately bounced to 표현집 below.
              next({ path: '/books/라이브 다시보기' });
          }
      }
  },
  {
    path: '/community',
    name: 'Community',
    component: () => import('@/views/CommunityPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/library',
    name: 'Library',
    component: () => import('@/views/LibraryPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/profile/edit',
    name: 'ProfileEdit',
    component: () => import('@/views/ProfileEditPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/welcome',
    name: 'Welcome',
    component: () => import('@/views/WelcomePage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  // ── Engine-chrome aliases (v5 routing ruling, 2026-08-16) ────────────────
  // Every engine tab and 더보기 destination renders inside the engine chrome
  // (global EngineTabBar via App.vue, keyed on the Engine* route name). Same
  // components, same guards — only the chrome differs. Legacy paths stay
  // reachable for the flip flag; nothing in engine chrome links to them.
  // At the flip these paths become canonical and the legacy names retire.
  {
    path: '/library-next',
    name: 'EngineLibrary',
    component: () => import('@/views/LibraryPage.vue'),
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    path: '/community-next',
    name: 'EngineCommunity',
    component: () => import('@/views/CommunityPage.vue'),
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    path: '/missions-next',
    name: 'EngineMissions',
    component: () => import('@/views/MissionArchivePage.vue'),
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    // 챌린지 tab (2026-10): its own data (cohortChallenges). The 영작 mission
    // routes below keep /challenges/expressionfeedback/… exactly as they were.
    path: '/challenges-next',
    name: 'EngineChallenges',
    component: () => import(/* webpackChunkName: "tab-challenges" */ '@/views/ChallengesView.vue'),
    meta: { requiresAuth: true, requiresPayment: true },
    beforeEnter: profileStepFirst
  },
  {
    // One challenge, a shareable link. The id is exactly 20 letters and digits
    // (a Firestore auto id): the pattern outranks the legacy
    // /challenges/:category/:id? and can never read 'expressionfeedback'.
    path: '/challenges-next/:id([A-Za-z0-9]{20})',
    name: 'EngineChallenge',
    component: () => import(/* webpackChunkName: "tab-challenges" */ '@/views/ChallengePageView.vue'),
    props: true,
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    path: '/challenges-next/expressionfeedback',
    name: 'EngineFeedback',
    component: () => import('@/views/ExpressionFbPage.vue'),
    props: true,
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    path: '/challenges-next/:category/:id?',
    name: 'EngineFeedbackDetail',
    component: () => import('@/views/ExpressionFbDetailPage.vue'),
    props: true,
    meta: { requiresAuth: true, requiresPayment: true },
    beforeEnter: (to, from, next) => {
      if (to.params.category.includes('expressionfeedback')) next();
      else next({ path: enginePath('/challenges/expressionfeedback') });
    }
  },
  {
    path: '/archive-next/:category/:id?',
    name: 'EngineBooks',
    component: () => import('@/views/PdfBookPage.vue'),
    props: true,
    meta: { requiresAuth: true, requiresPayment: true },
    beforeEnter: (to, from, next) => {
      const allowedCategories = ['비즈영어 표현집', '월간 실무 자료', '라이브 다시보기', '비즈 퀴즈'];
      if (allowedCategories.includes(to.params.category)) next();
      else next({ path: enginePath('/books/라이브 다시보기') });
    }
  },
  {
    path: '/profile-next',
    name: 'EngineProfile',
    component: () => import('@/views/ProfileEditPage.vue'),
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    path: '/payment-manage-next',
    name: 'EnginePaymentManage',
    component: () => import('@/views/PaymentManagePage.vue'),
    meta: { requiresAuth: true, requiresPayment: true }
  },
  {
    // 실전 기록 v2: the Reviewer's Desk — where 검토 중 posts are reviewed (admin only)
    path: '/admin-next/desk',
    name: 'EngineDesk',
    component: () => import('@/views/EngineDeskView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // the engine cockpit: is 2.0 being used, and who to nudge this week (2026-09-21)
    path: '/admin-next/engine',
    name: 'EngineAdminStats',
    component: () => import('@/views/AdminEngineView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/dashboard',
    name: 'EngineAdminDashboard',
    component: () => import('@/views/AdminDashboardPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, requiresOwner: true }
  },
  {
    path: '/admin-next/memberships',
    name: 'EngineAdminMemberships',
    component: () => import('@/views/AdminMembershipsPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/missions',
    name: 'EngineAdminMissions',
    component: () => import('@/views/AdminMissionsPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/invites',
    name: 'EngineAdminInvites',
    component: () => import('@/views/InviteManagementPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/analytics',
    name: 'EngineAdminAnalytics',
    component: () => import('@/views/AdminBookAnalyticsPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // 챌린지 admin (2026-10): the list, a new challenge, one challenge
    path: '/admin-next/challenges',
    name: 'EngineAdminChallenges',
    component: () => import('@/views/AdminChallengesView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/challenges/new',
    name: 'EngineAdminChallengeNew',
    component: () => import('@/views/AdminChallengeEditView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin-next/challenges/:id',
    name: 'EngineAdminChallengeEdit',
    component: () => import('@/views/AdminChallengeEditView.vue'),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // 관리자 › 새 챌린지 베타: the new program's mode and its beta accounts
    path: '/admin-next/beta',
    name: 'EngineAdminBeta',
    component: () => import('@/views/AdminBetaView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // 관리자 › 콘텐츠: the daily's content from Airtable, checked and 반영하기
    path: '/admin-next/content',
    name: 'EngineAdminContent',
    component: () => import('@/views/AdminContentView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // The new 챌린지 program's home (새 챌린지 in the beta). Never named Engine*:
    // canonicalRoutes leaves it be (it has no -next alias) and App.vue lights
    // the today tab for Today*. Its subpages (the daily, the 테스트, 보관함,
    // 피드백) arrive in their own PRs.
    path: '/today',
    name: 'Today',
    component: () => import('@/views/TodayHomeView.vue'),
    meta: { requiresAuth: true, requiresPayment: true },
    beforeEnter: [profileStepFirst, programGate]
  },

  // Legacy thread boards (자기소개 / 질문방 / 인증하기) are unified into 소통방.
  { path: '/threads/:category?/:threadId?', redirect: '/community' },
  // Older internal path while the feature was in flight — keep a redirect so
  // any bookmarks or screenshots that pointed to /sotongbang still resolve.
  { path: '/sotongbang', redirect: '/community' },
  {
    // 지난 미션 archive — the legacy home's scroll-back-through-every-mission
    // lane, moved off the home so this-week focus survives (docs D-2).
    path: '/missions',
    name: 'MissionArchive',
    component: () => import('@/views/MissionArchivePage.vue'),
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/challenges/expressionfeedback',
    name: 'ExpressionFeedback',
    component: () => import('@/views/ExpressionFbPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/challenges/:category/:id?',
    name: 'ExpressionFeedbackDetail',
    component: () => import('@/views/ExpressionFbDetailPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    },
    beforeEnter: (to, from, next) => {
      const category = to.params.category;
      if (category.includes('expressionfeedback')) {
        next(); // Allow navigation if category is in the allowed list
      } else {
        next({ path: '/challenges/expressionfeedback' }); // Redirect to /challenges page as default
      }
    }
  },
  {
    path: '/masterclass',
    name: 'Masterclass',
    component: () => import('@/views/MasterclassPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    // Routed lesson page — its OWN namespace (`/lesson/:id`), deliberately
    // NOT under `/masterclass`: vue-router 4 ranks the optional-param legacy
    // route `/masterclass/:category/:threadId?` above `/masterclass/:id`, which
    // shadowed the lesson and redirected it to the admin grid. A separate path
    // resolves cleanly and leaves the legacy `/masterclass/*` routes untouched.
    path: '/lesson/:id',
    name: 'Lesson',
    component: () => import('@/views/LessonView.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    }
  },
  {
    path: '/masterclass/:category/:threadId?',
    name: 'MasterClassThreads',
    component: () => import('@/views/ThreadsPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresPayment: true
    },
    beforeEnter: (to, from, next) => {
      const category = to.params.category;
      if (category.includes('masterclass-')) {
        next(); // Allow navigation if category is in the allowed list
      } else {
        next({ path: '/masterclass' }); // Redirect to /masterclass page as default
      }
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfService
  },
  {
    path: '/refund-policy',
    name: 'RefundPolicy',
    component: RefundPolicy
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },
];

// Create router instance
// ── The flip: the engine takes the original paths (Eddie, 2026-09-20) ────────
// With ENGINE_CUTOVER on, every Engine* route moves from its -next alias to
// the original path, the legacy route that owned that path retires, and the
// alias stays reachable as a redirect that keeps params, query and hash (a
// string redirect would drop ?tour=1). With the flag off this is the identity,
// so the flag-off build is untouched; engine.flagoff.spec pins that.
function canonicalRoutes(list) {
  if (!ENGINE_CUTOVER) return list;
  const isEngine = (r) => String(r.name || '').startsWith('Engine');
  const owned = new Set();
  for (const r of list) if (isEngine(r)) { const c = toCanonical(r.path); if (c !== r.path) owned.add(c); }
  const out = [];
  for (const r of list) {
    if (!isEngine(r) && owned.has(r.path)) continue; // the legacy twin retires
    if (isEngine(r)) { const c = toCanonical(r.path); if (c !== r.path) { out.push({ ...r, path: c }); continue; } }
    out.push(r);
  }
  for (const r of list) {
    if (!isEngine(r)) continue;
    const c = toCanonical(r.path);
    if (c === r.path) continue;
    // params are rebuilt from to.params (decoded), never from to.path
    // (encoded), so a Korean category is not encoded twice
    out.push({
      path: r.path,
      redirect: (to) => {
        const base = c.replace(/\/:[^/]+/g, '');
        const segs = Object.values(to.params || {}).filter((v) => v !== undefined && v !== '');
        return { path: segs.length ? `${base}/${segs.join('/')}` : base, query: to.query, hash: to.hash };
      },
    });
  }
  return out;
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: canonicalRoutes(routes)
});

// Wait up to 2s for Firebase auth to settle. On client-side navigation
// `auth.currentUser` can be null for a tick until onAuthStateChanged
// re-emits — without this, requiresAuth routes bounce members to `/`
// during fast navigation and a manual reload "fixes" it.
function authReady(timeoutMs = 2000) {
  return new Promise((resolve) => {
    if (auth.currentUser) return resolve(auth.currentUser);
    let settled = false;
    const unsub = auth.onAuthStateChanged((user) => {
      if (settled) return;
      settled = true;
      try { unsub(); } catch (e) { /* ignore */ }
      resolve(user || null);
    });
    setTimeout(() => {
      if (settled) return;
      settled = true;
      try { unsub(); } catch (e) { /* ignore */ }
      resolve(auth.currentUser || null);
    }, timeoutMs);
  });
}

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresOwner = to.matched.some(record => record.meta.requiresOwner);
  const requiresPayment = to.matched.some(record => record.meta.requiresPayment);
  const isAuthenticated = await authReady();

  if (requiresAuth && !isAuthenticated) {
    next('/');
    return;
  }

  if (requiresOwner && isAuthenticated?.email !== 'sung.ws.lee@gmail.com') {
    next('/home');
    return;
  }

  if (requiresAdmin && isAuthenticated) {
    // Check if user is admin
    try {
      const { default: userService } = await import('@/api/userService');
      const userInfo = await userService.getUser(isAuthenticated.uid);
      
      if (userInfo?.tier !== 'admin' && userInfo?.tier !== 'owner') {
        next('/home');
        return;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      next('/home');
      return;
    }
  }

  if(requiresPayment && isAuthenticated) {
    try {
      const { default: userService } = await import('@/api/userService');
      const userInfo = await userService.getUser(isAuthenticated.uid);

      if (userInfo?.paymentStatus === 'unpaid') {
        next('/payment');
        return;
      }

      if (userInfo?.paymentStatus === 'paused') {
        const toMs = (v) =>
          v?.toDate?.().getTime?.() ??
          (typeof v === 'number' ? v : null) ??
          (typeof v === 'string' ? Date.parse(v) : null);

        const billing = await userService.getUserBilling(isAuthenticated.uid);

        const cutoffMs = toMs(billing?.nextChargeAt);
        console.log(cutoffMs);

        if (!cutoffMs || Date.now() >= cutoffMs) {
          next('/payment');
          return;
        }
      }

      if (userInfo?.paymentStatus === '' && userInfo?.freeAccessUntil && new Date(userInfo.freeAccessUntil) < new Date()) {
        next('/payment');
        return;
      }

      // Tier-0 members only have 비즈영어 표현집. Inside this requiresPayment
      // block, the only route they should reach is `Books`; everything else
      // (Home, Community, Masterclass, …) is locked content — funnel them
      // back to their one destination.
      // 'Books' before the flip, 'EngineBooks' after it: the same page at the
      // same path. Recognising only one name loops a tier-0 member forever.
      if (userInfo?.tier === '0' && to.name !== 'Books' && to.name !== 'EngineBooks') {
        next('/books/비즈영어 표현집');
        return;
      }

      // New-member welcome: intercept the first home load when the member
      // hasn't seen the welcome step yet AND profile is incomplete. Either
      // action on /welcome stamps profileOnboardingShownAt so this only
      // fires once per account.
      //
      // BOTH homes (cutover audit 2026-09-16): behind ENGINE_CUTOVER the
      // bookmarked /home is a redirect, so the route that arrives here is
      // named EngineHome. Keyed on 'Home' alone, the flip would have sent
      // every new member straight past the profile step — and since the
      // interception only fires while the stamp is absent, they would never
      // have been asked again.
      if (
        (to.name === 'Home' || to.name === 'EngineHome') &&
        userInfo &&
        !userInfo.profileCompletedAt &&
        !userInfo.profileOnboardingShownAt
      ) {
        next('/welcome');
        return;
      }
    } catch (error) {
      // Fail-open on transient Firestore read errors. Real unpaid members
      // still get blocked above; trapping the page on every flaky read
      // was bouncing logged-in members to /payment unnecessarily.
      console.warn('[router] payment-check read failed; fail-open:', error?.message || error);
    }
  }

  next();
});

// Page views are logged from App.vue (G3, 2026-09-07): it watches the route
// AND auth, so the first view of a session — when auth has not resolved by
// afterEach — is no longer dropped.

export default router;
